import React, { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from './firebase';
import { compressImages } from './imageCompression';
import { useToast } from './Toast';
import {
  PICKUP,
  RETURN,
  MAX_PHOTOS_PER_REPORT,
  describeStatus,
  describeWindow,
  getWindowState,
  needsMyResponse,
  roleFor,
  statusTone,
} from './conditionReports';

const TONE_COLORS = {
  good: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
  warn: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
  pending: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
  neutral: { bg: '#f9fafb', border: '#e5e7eb', text: '#4b5563' },
};

function StatusPill({ report }) {
  const tone = TONE_COLORS[statusTone(report)] || TONE_COLORS.neutral;
  return (
    <span
      style={{
        padding: '3px 10px',
        borderRadius: '999px',
        fontSize: '0.78rem',
        fontWeight: 700,
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
        whiteSpace: 'nowrap',
      }}
    >
      {describeStatus(report)}
    </span>
  );
}

function PhotoGrid({ photos = [] }) {
  if (!photos.length) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
        gap: '8px',
        marginTop: '10px',
      }}
    >
      {photos.map((photo, index) => (
        <a
          key={`${photo.storagePath || photo.url}-${index}`}
          href={photo.url}
          target="_blank"
          rel="noopener noreferrer"
          title={`Added by the ${photo.uploadedByRole}`}
          style={{ display: 'block' }}
        >
          <img
            src={photo.url}
            alt={`Gear condition recorded by the ${photo.uploadedByRole}`}
            style={{
              width: '100%',
              aspectRatio: '1 / 1',
              objectFit: 'cover',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              display: 'block',
            }}
          />
          <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{photo.uploadedByRole}</span>
        </a>
      ))}
    </div>
  );
}

function PhaseSection({ rental, phase, report, myRole, onChanged }) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const inputId = `condition-${phase}-${rental.id}`;

  const windowState = getWindowState(rental, phase);
  const photos = report?.photos || [];
  const canAdd = windowState === 'open' && photos.length < MAX_PHOTOS_PER_REPORT;
  const owesResponse = needsMyResponse(report, myRole);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    if (photos.length + files.length > MAX_PHOTOS_PER_REPORT) {
      showToast(`A report holds at most ${MAX_PHOTOS_PER_REPORT} photos.`, 'error');
      return;
    }

    setBusy(true);
    try {
      const prepared = await compressImages(files);
      const uploaded = [];

      for (const file of prepared) {
        const safeName = (file.name || 'photo.jpg').replace(/[^\w.-]/g, '_');
        const storagePath = `conditionPhotos/${rental.id}/${rental.viewerId}/${Date.now()}_${safeName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        uploaded.push({ url: await getDownloadURL(storageRef), storagePath });
      }

      const attach = httpsCallable(functions, 'attachConditionPhotos');
      await attach({ rentalId: rental.id, phase, photos: uploaded });
      showToast(
        `${uploaded.length} photo${uploaded.length === 1 ? '' : 's'} added.`,
        'success'
      );
      onChanged?.();
    } catch (error) {
      showToast(error.message || 'Could not add those photos.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const respond = async (agrees) => {
    let note = '';
    if (!agrees) {
      note = (window.prompt('Briefly, what looks different from these photos?') || '').trim();
      if (!note) return;
      if (note.length < 5) {
        showToast('Please add a little more detail so the other person can respond.', 'error');
        return;
      }
    } else if (
      !window.confirm(
        'Confirm these photos match the gear in front of you right now?\n\nThis becomes the agreed record of its condition.'
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      const send = httpsCallable(functions, 'respondToConditionReport');
      await send({ rentalId: rental.id, phase, agrees, note });
      showToast(agrees ? 'Confirmed. Thank you.' : 'Reported — the other person has been told.', 'success');
      onChanged?.();
    } catch (error) {
      showToast(error.message || 'Could not send that.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const label = phase === PICKUP ? 'At pickup' : 'At return';

  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: '10px',
        border: '1px solid #e5e7eb',
        backgroundColor: owesResponse ? '#fffbeb' : '#fff',
        marginTop: '10px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <strong style={{ color: '#1f2937' }}>{label}</strong>
        <StatusPill report={report} />
      </div>

      <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: '0.86rem' }}>
        {describeWindow(rental, phase)}
      </p>

      <PhotoGrid photos={photos} />

      {owesResponse && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: '#fff7ed',
            border: '1px solid #fed7aa',
          }}
        >
          <p style={{ margin: '0 0 10px', color: '#7c2d12', fontSize: '0.92rem', lineHeight: 1.5 }}>
            <strong>Does this match the gear in front of you?</strong> Look at the photos above
            before answering — this becomes the agreed record of its condition.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => respond(true)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: busy ? '#9ca3af' : '#059669',
                color: '#fff',
                fontWeight: 700,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              Yes, this matches
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => respond(false)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #dc2626',
                backgroundColor: '#fff',
                color: '#b91c1c',
                fontWeight: 700,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              Something's different
            </button>
          </div>
        </div>
      )}

      {report?.ownerNote && (
        <p style={{ margin: '8px 0 0', color: '#991b1b', fontSize: '0.86rem' }}>
          Owner noted: {report.ownerNote}
        </p>
      )}
      {report?.renterNote && (
        <p style={{ margin: '4px 0 0', color: '#991b1b', fontSize: '0.86rem' }}>
          Renter noted: {report.renterNote}
        </p>
      )}

      {canAdd && (
        <div style={{ marginTop: '12px' }}>
          <input
            id={inputId}
            type="file"
            accept="image/*,.heic,.heif"
            /* Opens the camera directly on phones. A convenience, not a guarantee —
               the real protection is the time window and the other party confirming. */
            capture="environment"
            multiple
            onChange={handleFiles}
            disabled={busy}
            style={{ display: 'none' }}
          />
          <label
            htmlFor={inputId}
            style={{
              display: 'inline-block',
              padding: '10px 16px',
              borderRadius: '8px',
              backgroundColor: busy ? '#9ca3af' : '#2563eb',
              color: '#fff',
              fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: '0.92rem',
            }}
          >
            {busy ? 'Uploading…' : photos.length ? 'Add more photos' : 'Add photos'}
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * The photo record of a rental handoff. Shown to both parties on an approved
 * rental; each phase opens and closes on its own schedule.
 */
export default function ConditionReportCard({ rental, userId }) {
  const [reports, setReports] = useState({});
  const myRole = roleFor(rental, userId);

  const subscribe = useCallback(() => {
    if (!rental?.id || !myRole) return undefined;
    const unsubscribers = [PICKUP, RETURN].map((phase) =>
      onSnapshot(
        doc(db, 'rentals', rental.id, 'conditionReports', phase),
        (snap) => {
          setReports((prev) => ({ ...prev, [phase]: snap.exists() ? snap.data() : null }));
        },
        () => {
          setReports((prev) => ({ ...prev, [phase]: null }));
        }
      )
    );
    return () => unsubscribers.forEach((fn) => fn());
  }, [rental?.id, myRole]);

  useEffect(() => subscribe(), [subscribe]);

  if (!myRole || !rental) return null;
  if (rental.status !== 'approved' && rental.status !== 'completed') return null;

  const bothClosed =
    getWindowState(rental, PICKUP) === 'closed' && getWindowState(rental, RETURN) === 'closed';
  const anyPhotos = (reports[PICKUP]?.photos || []).length || (reports[RETURN]?.photos || []).length;
  if (bothClosed && !anyPhotos) return null;

  const rentalWithViewer = { ...rental, viewerId: userId };

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
      }}
    >
      <h4 style={{ margin: 0, color: '#1f2937', fontSize: '1rem' }}>Condition photos</h4>
      <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: '0.88rem', lineHeight: 1.5 }}>
        Photograph the gear together at handoff and again at return. Both of you can add photos and
        confirm them. Damage claims require photos from both, so this is what protects each of you
        if something goes wrong.
      </p>

      <PhaseSection
        rental={rentalWithViewer}
        phase={PICKUP}
        report={reports[PICKUP]}
        myRole={myRole}
      />
      <PhaseSection
        rental={rentalWithViewer}
        phase={RETURN}
        report={reports[RETURN]}
        myRole={myRole}
      />
    </div>
  );
}
